import { isEventLiveToday, isEventPast } from '@moment-golf/shared';

export type EventPublishIconKind = 'canceled' | 'published' | 'pending';
export type EventStatusLabel = 'Live' | 'Completed' | 'Cancelled';

export function getEventPublishIconKind(event: {
    published?: boolean;
    canceled?: boolean;
}): EventPublishIconKind {
    if (event.canceled) return 'canceled';
    if (event.published) return 'published';
    return 'pending';
}

/** Tooltip: status label plus the condition that selected the publish icon. */
export function getEventPublishIconTooltip(kind: EventPublishIconKind): string {
    switch (kind) {
        case 'canceled':
            return 'Cancelled — shown when the event is canceled';
        case 'published':
            return 'Published — shown when the event is published and not canceled';
        case 'pending':
            return 'Not published — shown when the event is unpublished and not canceled';
    }
}

/** Status chip label, or null when the event is upcoming (not live / past / canceled). */
export function getEventStatusLabel(event: {
    startDate: string;
    endDate: string;
    canceled?: boolean;
}): EventStatusLabel | null {
    if (event.canceled) return 'Cancelled';
    if (isEventLiveToday(event.startDate, event.endDate)) return 'Live';
    if (isEventPast(event.endDate)) return 'Completed';
    return null;
}

/** Live = green/success, Completed = sepia, Cancelled = error/red. */
export function getEventStatusChipSx(label: EventStatusLabel, opts?: { fontSize?: number; mr?: number }) {
    const fontSize = opts?.fontSize ?? 12;
    const mr = opts?.mr;
    const base = {
        height: 18,
        fontSize,
        fontFamily: 'Fredoka',
        border: '1px solid',
        ...(mr != null ? { mr } : {}),
        '& .MuiChip-label': { px: 1, py: 0 },
    };

    if (label === 'Live') {
        return {
            ...base,
            backgroundColor: 'success.light',
            color: 'common.white',
            borderColor: 'success.main',
        };
    }
    if (label === 'Completed') {
        return {
            ...base,
            backgroundColor: 'sepia.background',
            color: 'sepia.main',
            borderColor: 'sepia.main',
        };
    }
    return {
        ...base,
        backgroundColor: 'error.extraLight',
        color: 'error.main',
        borderColor: 'error.main',
    };
}

export function getEventFillRate(playerCap?: number | null, registrationCount?: number | null): number {
    const cap = playerCap ?? 0;
    if (cap <= 0) return 0;
    const count = registrationCount ?? 0;
    return Math.min(100, Math.max(0, (count / cap) * 100));
}
